// src/services/consentCore.js
//
// PORTABLE MODULE — copy this file as-is into any Firebase (v9 modular SDK) project.
// Do not edit this file per-app; configure behavior via createConsentService(firestore, config)
// in that app's own thin wrapper file instead.

import {
  doc, getDoc, setDoc, updateDoc, serverTimestamp,
} from 'firebase/firestore';

const DEFAULT_TNC_CONTENT = `By continuing to use this platform, you confirm that you have reviewed the Terms & Conditions and agree to be bound by them.

This application is provided for compliance and audit workflows. You agree to use it lawfully, keep credentials confidential, and provide accurate information. The platform may process operational, identity, and compliance-related metadata necessary for account access, reporting, and audit support.

You understand that your continued use of the platform is subject to applicable laws, security policies, and the organization's operational rules. If the Terms & Conditions are updated, your continued access means you accept the latest version.`;

async function getClientIP() {
  try {
    const res = await fetch('https://api.ipify.org?format=json');
    const { ip } = await res.json();
    return ip || 'unknown';
  } catch (err) {
    console.error('IP lookup failed:', err);
    return 'unknown';
  }
}

async function getBrowserLocation() {
  if (!navigator || !navigator.geolocation) return null;

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`
          );
          if (!res.ok) { resolve(null); return; }
          const data = await res.json();
          const address = data?.address || {};
          resolve({
            city: address.city || 'unknown',
            state: address.state || address.region || 'unknown',
          });
        } catch (err) {
          console.error('Browser geolocation reverse lookup failed:', err);
          resolve(null);
        }
      },
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 600000 }
    );
  });
}

async function getLocationFromIP(ipAddress) {
  if (!ipAddress || ipAddress === 'unknown') {
    return { city: 'unknown', state: 'unknown', country: 'unknown', ipAddress: 'unknown' };
  }
  try {
    const res = await fetch(`https://ipapi.co/${ipAddress}/json/`);
    if (!res.ok) throw new Error('Location lookup failed');
    const data = await res.json();
    return {
      city: data.city || 'unknown',
      state: data.region || data.region_code || 'unknown',
      country: data.country_name || 'unknown',
      ipAddress,
    };
  } catch (err) {
    console.error('Geo lookup failed:', err);
    return { city: 'unknown', state: 'unknown', country: 'unknown', ipAddress };
  }
}

async function getClientLocation() {
  const ipAddress = await getClientIP();
  const browserLocation = await getBrowserLocation();

  if (browserLocation && (browserLocation.city !== 'unknown' || browserLocation.state !== 'unknown')) {
    return { ...browserLocation, ipAddress };
  }
  return getLocationFromIP(ipAddress);
}

/**
 * @param {Firestore} firestore
 * @param {Object} [config]
 * @param {string} [config.userCollection='UserProfileMaster']
 * @param {string} [config.settingsCollection='AppSettings']
 * @param {string} [config.settingsDocId='legal']
 * @param {string} [config.historyField='tncAcknowledgmentHistory']
 * @param {number} [config.historyLimit=3]
 * @param {number} [config.reconsentIntervalDays=183] — ~6 months
 */
export function createConsentService(firestore, config = {}) {
  const USER_COLLECTION = config.userCollection || 'UserProfileMaster';
  const SETTINGS_COLLECTION = config.settingsCollection || 'AppSettings';
  const SETTINGS_DOC_ID = config.settingsDocId || 'legal';
  const HISTORY_FIELD = config.historyField || 'tncAcknowledgmentHistory';
  const HISTORY_LIMIT = config.historyLimit ?? 3;
  const RECONSENT_INTERVAL_DAYS = config.reconsentIntervalDays ?? 183;
  const TNC_COLLECTION = config.tncCollection || 'TnCVersions';
  const CONSENT_SUBCOLLECTION = config.consentSubcollection || 'legalConsents';

  // ---- Read the current Legal settings (T&C, Privacy Policy, Third-Party) ----
  async function getLegalSettings() {
    try {
      const ref = doc(firestore, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
      const snap = await getDoc(ref);

      if (!snap.exists()) {
        console.warn('[consentCore] AppSettings/legal not found — using built-in default T&C.');
        return {
          tncContent: DEFAULT_TNC_CONTENT,
          tncVersion: 1,
          tncUpdatedAt: null,
          privacyPolicyContent: '',
          thirdPartyLicensingContent: '',
          additionalRemarks: '-',
          fallback: true,
        };
      }

      const data = snap.data();
      return {
        tncContent: data.tncContent || DEFAULT_TNC_CONTENT,
        tncVersion: data.tncVersion ?? 1,
        tncUpdatedAt: data.tncUpdatedAt || null,
        privacyPolicyContent: data.privacyPolicyContent || '',
        thirdPartyLicensingContent: data.thirdPartyLicensingContent || '',
        additionalRemarks: data.additionalRemarks || '-',
      };
    } catch (err) {
      console.warn('[consentCore] Failed to read AppSettings/legal, using fallback.', err);
      return {
        tncContent: DEFAULT_TNC_CONTENT,
        tncVersion: 1,
        tncUpdatedAt: null,
        privacyPolicyContent: '',
        thirdPartyLicensingContent: '',
        additionalRemarks: '-',
        fallback: true,
      };
    }
  }

  // ---- SuperAdmin: save updated Legal settings ----
  // Only bumps tncVersion (and forces re-consent) when the T&C text itself changed.
  // Editing Privacy Policy / Third-Party text alone does not trigger re-consent.
  async function saveLegalSettings({
    tncContent, privacyPolicyContent, thirdPartyLicensingContent, additionalRemarks, updatedByEmail,
  }) {
    const ref = doc(firestore, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
    const snap = await getDoc(ref);
    const existing = snap.exists() ? snap.data() : {};

    const existingTrimmed = (existing.tncContent || '').trim();
    const newTrimmed = (tncContent || '').trim();
    const tncChanged = existingTrimmed !== newTrimmed;

    console.info(
      `[consentCore] saveLegalSettings — content changed: ${tncChanged} | existing length: ${existingTrimmed.length} | new length: ${newTrimmed.length}`
    );

    const nextVersion = tncChanged ? (existing.tncVersion || 0) + 1 : (existing.tncVersion || 1);

    const payload = {
      tncContent: tncContent || '',
      tncVersion: nextVersion,
      privacyPolicyContent: privacyPolicyContent || '',
      thirdPartyLicensingContent: thirdPartyLicensingContent || '',
      additionalRemarks: additionalRemarks?.trim() || '-',
      updatedBy: updatedByEmail || 'unknown',
      updatedAt: serverTimestamp(),
    };

    if (tncChanged) {
      payload.tncUpdatedAt = serverTimestamp();
    }

    await setDoc(ref, payload, { merge: true });
    return { success: true, tncVersion: nextVersion, tncChanged };
  }

  // ---- Does this user need to (re-)accept? ----
  async function checkTncVersionMatch(userDocId) {
    const settings = await getLegalSettings();

    if (!userDocId) {
      return { matches: false, activeVersion: settings.tncVersion, reason: 'missing_profile_id' };
    }

    const userSnap = await getDoc(doc(firestore, USER_COLLECTION, userDocId));
    if (!userSnap.exists()) {
      return { matches: false, activeVersion: settings.tncVersion, reason: 'profile_missing' };
    }

    const profile = userSnap.data();
    const lastAccepted = profile.lastAcceptedTnCVersion;
    const lastAcceptedAt = profile.lastAcceptedTnCAt;

    console.info(
      `[consentCore] checkTncVersionMatch — profile: ${userDocId} | lastAccepted: ${lastAccepted} | current: ${settings.tncVersion}`
    );

    if (lastAccepted === undefined || lastAccepted === null) {
      return { matches: false, activeVersion: settings.tncVersion, reason: 'no_accepted_version' };
    }

    const versionMatches = Number(lastAccepted) === Number(settings.tncVersion);

    // Even if the version hasn't changed, force a fresh acknowledgement after
    // the re-consent interval (default ~6 months) has elapsed.
    if (versionMatches && lastAcceptedAt?.toDate) {
      const daysSince = (Date.now() - lastAcceptedAt.toDate().getTime()) / (1000 * 60 * 60 * 24);
      if (daysSince >= RECONSENT_INTERVAL_DAYS) {
        console.info(`[consentCore] Re-consent interval elapsed (${Math.floor(daysSince)} days). Forcing review.`);
        return { matches: false, activeVersion: settings.tncVersion, reason: 'reconsent_interval_elapsed' };
      }
    }

    return { matches: versionMatches, activeVersion: settings.tncVersion };
  }

  // ---- Record an acceptance ----
  // Snapshots the exact T&C text into the user's own history (capped at
  // HISTORY_LIMIT entries), so what they agreed to is preserved forever even
  // if the current AppSettings text is edited later.
  async function recordConsent(userDocId, manualLocation = null) {
    if (!userDocId) {
      throw new Error('Consent write requires a valid user profile.');
    }

    const settings = await getLegalSettings();
    const location = manualLocation || await getClientLocation();
    const userAgent = navigator.userAgent;

    const userRef = doc(firestore, USER_COLLECTION, userDocId);
    const userSnap = await getDoc(userRef);
    const profile = userSnap.exists() ? userSnap.data() : {};

    const newEntry = {
      version: settings.tncVersion,
      acceptedAt: new Date(), // client-supplied — see note on serverTimestamp() limitation in arrays
      contentEffectiveDate: settings.tncUpdatedAt?.toDate ? settings.tncUpdatedAt.toDate() : new Date(),
      content: settings.tncContent,
      ipAddress: location.ipAddress,
      city: location.city,
      state: location.state,
      userAgent,
      action: 'accepted',
    };

    const existingHistory = Array.isArray(profile[HISTORY_FIELD]) ? profile[HISTORY_FIELD] : [];
    const nextHistory = [newEntry, ...existingHistory].slice(0, HISTORY_LIMIT);

    await updateDoc(userRef, {
      lastAcceptedTnCVersion: settings.tncVersion,
      lastAcceptedTnCAt: serverTimestamp(), // authoritative, real server clock — used by the interval check above
      lastAcceptedTnCIP: location.ipAddress,
      lastAcceptedTnCLocation: { city: location.city, state: location.state },
      [HISTORY_FIELD]: nextHistory,
    });

    return { success: true, version: settings.tncVersion };
  }

  async function recordLogin(userDocId) {
    const ip = await getClientIP();
    await updateDoc(doc(firestore, USER_COLLECTION, userDocId), {
      lastLoginAt: serverTimestamp(),
      lastLoginIP: ip,
   
    });
  }

  // ---- Read this user's own acceptance history (last 3) ----
  async function getConsentHistory(userDocId) {
    const snap = await getDoc(doc(firestore, USER_COLLECTION, userDocId));
    if (!snap.exists()) return [];
    const history = snap.data()[HISTORY_FIELD];
    return Array.isArray(history) ? history : [];
  }

  // Marks the entire TENANT (organization) for deletion — not just the
  // requesting user's own profile — since deleting an AppAdmin's account
  // should offboard the whole org. tenantCollection defaults to 'Tenants'.
  async function requestAccountDeletion(tenantId, requestedByEmail, tenantCollection = 'Tenants') {
    if (!tenantId) {
      throw new Error('Account deletion requires a valid tenant ID.');
    }

    const purgeDate = new Date();
    purgeDate.setDate(purgeDate.getDate() + 30);

    await updateDoc(doc(firestore, tenantCollection, tenantId), {
      status: 'marked_for_deletion',
      deletionMarkedAt: serverTimestamp(),
      deletionScheduledFor: purgeDate,
      deletionRequestedBy: requestedByEmail || 'unknown',
    });

    return { success: true, purgeScheduledFor: purgeDate.toISOString() };
  }

  return {
    getLegalSettings,
    saveLegalSettings,
    checkTncVersionMatch,
    recordConsent,
    recordLogin,
    getConsentHistory,
    requestAccountDeletion,
    getClientLocation,
  };
}